import React from 'react';
import { OverflowRow } from '../shared/OverflowRow';
import {
  PRIORITY_ORDER, PRIORITY_LABEL, PRIORITY_COLOR,
  TIER_ORDER, TIER_LABEL, TIER_COLOR,
  type PriorityKey, type TierKey,
} from './clientViewTokens';

interface ChipRowProps<K extends string> {
  label: string;
  options: readonly K[];
  counts: Record<K, number>;
  selected: K[];
  onToggle: (key: K) => void;
  labelMap: Record<K, string>;
  colorMap: Record<K, string | { fg: string }>;
  disabled?: boolean;
  disabledHint?: string;
}

function ChipRow<K extends string>({
  label, options, counts, selected, onToggle, labelMap, colorMap, disabled, disabledHint,
}: ChipRowProps<K>) {
  const items = options.map((key) => {
    const count = counts[key] ?? 0;
    const isActive = selected.includes(key);
    const color = colorMap[key];
    const dotColor = typeof color === 'string' ? color : color.fg;
    return {
      key,
      active: isActive,
      node: (
        <button
          type="button"
          className={`cv-chip ${isActive ? 'active' : ''}`}
          style={{ color: dotColor }}
          onClick={() => onToggle(key)}
          aria-pressed={isActive}
          disabled={disabled}
        >
          <span className="cv-chip-dot" />
          <span style={{ color: 'var(--color-text-primary, #222)' }}>{labelMap[key]}</span>
          <span className="cv-chip-count">{count}</span>
        </button>
      ),
    };
  });

  return (
    <OverflowRow
      className={`cv-chip-row${disabled ? ' cv-chip-row-disabled' : ''}`}
      title={disabled ? disabledHint : undefined}
      label={
        disabled && disabledHint ? (
          // A title on the row can't explain the greying: browsers suppress
          // tooltips over disabled buttons, so the chips just read as broken.
          <span className="cv-chip-row-label-wrap">
            {label}
            <span className="cv-chip-row-note" title={disabledHint}>n/a in this view</span>
          </span>
        ) : (
          label
        )
      }
      items={items}
    />
  );
}

export interface PriorityChipRowProps {
  counts: Record<PriorityKey, number>;
  selected: PriorityKey[];
  onToggle: (key: PriorityKey) => void;
  disabled?: boolean;
  disabledHint?: string;
}

export const PriorityChipRow: React.FC<PriorityChipRowProps> = ({ counts, selected, onToggle, disabled, disabledHint }) => (
  <ChipRow
    label="Priority"
    options={PRIORITY_ORDER}
    counts={counts}
    selected={selected}
    onToggle={onToggle}
    labelMap={PRIORITY_LABEL}
    colorMap={PRIORITY_COLOR}
    disabled={disabled}
    disabledHint={disabledHint}
  />
);

export interface TierChipRowProps {
  /** Defaults to "Tier"; the board passes the window it reads, e.g. "Tier (quarter)". */
  label?: string;
  counts: Record<TierKey, number>;
  selected: TierKey[];
  onToggle: (key: TierKey) => void;
  disabled?: boolean;
  disabledHint?: string;
}

export const TierChipRow: React.FC<TierChipRowProps> = ({ label = 'Tier', counts, selected, onToggle, disabled, disabledHint }) => (
  <ChipRow
    label={label}
    options={TIER_ORDER}
    counts={counts}
    selected={selected}
    onToggle={onToggle}
    labelMap={TIER_LABEL}
    colorMap={TIER_COLOR}
    disabled={disabled}
    disabledHint={disabledHint}
  />
);
